<template>
  <vs-popup :active.sync="isActive" title="" @close="close">
    <div class="details-popup" v-if="modalData">
      <button class="popup-close" @click="close">✕</button>

      <!-- Hero header -->
      <div class="details-hero">
        <div class="details-hero-left">
          <span class="closure-badge">Cierre #{{ modalData.closure_number }}</span>
          <div class="company-name">{{ companyName }}</div>
          <div v-if="companyLegalName" class="company-meta">
            <span class="company-meta-label">Razón Social</span>
            <span class="company-legal">{{ companyLegalName }}</span>
          </div>
          <div v-if="modalData.responsible_agency" class="company-meta">
            <span class="company-meta-label">Agencia Responsable</span>
            <span class="company-legal">{{ modalData.responsible_agency.name }}</span>
          </div>
          <div class="campaign-ref">
            Campaña ID {{ modalData.campaign_id }}
            <span v-if="modalData.campaign_name"> — {{ modalData.campaign_name }}</span>
            <span class="campaign-type-badge" :class="{ 'campaign-type-badge--unknown': !modalData.campaign_type }"><span class="campaign-type-prefix">Tipo ·</span> {{ campaignTypeLabel }}</span>
          </div>
        </div>
        <div class="details-hero-right">
          <div class="amount-display">{{ formatAmount(chargeTotal, modalData.currency) }}</div>
          <vs-chip :color="chargeStatusColor" textWhite class="status-badge">
            {{ chargeStatusLabel }}
          </vs-chip>
        </div>
      </div>

      <!-- Info grid: 6-col -->
      <div class="info-grid">
        <div class="info-card info-card--span3">
          <span class="info-label">Período</span>
          <span class="info-value">{{ formatDate(modalData.date_from) }} → {{ formatDate(modalData.date_to) }}</span>
        </div>
        <div class="info-card info-card--span3">
          <span class="info-label">Fecha Vencimiento Cobro</span>
          <span class="info-value">{{ formatDate(modalData.collection_due_date) }}</span>
        </div>
        <div class="info-card info-card--span2">
          <span class="info-label">Días Faltantes</span>
          <span :class="['info-value', daysUntilCollection.includes('vencido') ? 'text-danger' : daysUntilCollection === 'Hoy' ? 'text-warning' : '']">
            {{ daysUntilCollection }}
          </span>
        </div>
        <div class="info-card info-card--span2">
          <span class="info-label">Plazo Facturación</span>
          <span class="info-value">{{ modalData.charge_term }} días</span>
        </div>
        <div class="info-card info-card--span2">
          <span class="info-label">Plazo de Pago</span>
          <span class="info-value">{{ modalData.payment_term }} días</span>
        </div>
      </div>

      <!-- Modes strip -->
      <div class="modes-strip">
        <div class="mode-item">
          <span class="mode-label">Facturación</span>
          <span class="mode-value">{{ billingModeLabel }}</span>
        </div>
        <span class="mode-sep">·</span>
        <div class="mode-item">
          <span class="mode-label">Pago</span>
          <span class="mode-value">{{ paymentModeLabel }}</span>
        </div>
        <span class="mode-sep">·</span>
        <div class="mode-item">
          <span class="mode-label">Comisión</span>
          <span class="mode-value">{{ commissionModeLabel }}</span>
        </div>
      </div>

      <!-- Estado de facturación -->
      <div class="billing-status-bar" v-if="modalData.billing_status">
        <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4" />
        <span>Facturación:</span>
        <vs-chip :color="billingStatusColor" textWhite size="small">{{ billingStatusLabel }}</vs-chip>
        <span v-if="modalData.billing" class="billing-by">
          — {{ formatUserName(modalData.billing.created_by) }}, {{ formatDateTime(modalData.billing.created_at) }}
        </span>
      </div>

      <!-- Cobrado por -->
      <div v-if="isCollected && modalData.charge" class="billed-info">
        <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4 text-success" />
        <span>Cobrado por <strong>{{ formatUserName(modalData.charge.created_by) }}</strong> el {{ formatDateTime(modalData.charge.created_at) }}</span>
      </div>

      <!-- Anulado -->
      <div v-if="modalData.charge && modalData.charge.canceled_at" class="canceled-info">
        <feather-icon icon="XCircleIcon" svgClasses="w-4 h-4" />
        <span>Anulado por <strong>{{ formatUserName(modalData.charge.canceled_by) }}</strong> el {{ formatDateTime(modalData.charge.canceled_at) }}</span>
      </div>

      <!-- Resumen financiero -->
      <div class="summary-section" v-if="modalData.summary">
        <div class="summary-title">Resumen financiero</div>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">Total a Cobrar</span>
            <span class="summary-value primary">{{ formatAmount(modalData.summary.billing_amount, modalData.currency) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Comisión Agencia</span>
            <div class="summary-value-row">
              <span class="summary-value">{{ formatAmount(modalData.summary.agency_commission_payment, modalData.currency) }}</span>
              <span v-if="modalData.agency_commission_percentage" class="summary-pct">{{ modalData.agency_commission_percentage }}%</span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Comisión Exchange</span>
            <div class="summary-value-row">
              <span class="summary-value">{{ formatAmount(modalData.summary.exchange_commission_payment, modalData.currency) }}</span>
              <span v-if="modalData.exchange_commission_percentage" class="summary-pct">{{ modalData.exchange_commission_percentage }}%</span>
            </div>
          </div>
          <div class="summary-item">
            <span class="summary-label">Impuesto Internacional</span>
            <div class="summary-value-row">
              <span class="summary-value">{{ formatAmount(modalData.summary.international_tax_payment, modalData.currency) }}</span>
              <span v-if="modalData.international_tax_percentage" class="summary-pct">{{ modalData.international_tax_percentage }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Creado por -->
      <div v-if="modalData.created_by" class="created-by-info">
        <feather-icon icon="UserIcon" svgClasses="w-3 h-3" />
        <span>Creado por <strong>{{ formatUserName(modalData.created_by) }}</strong> el {{ formatDateTime(modalData.created_at) }}</span>
      </div>

    </div>
  </vs-popup>
</template>

<script>
import {
  formatAmount,
  formatDate,
  formatDateTime,
  formatUserName
} from '@/utils/billingAndCharges'
import { CHARGE_STATUS, CHARGE_STATUS_LABELS, CHARGE_STATUS_COLORS, BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, BILLING_MODE_LABELS, PAYMENT_MODE_LABELS, AGENCY_COMMISSION_MODE_LABELS, CAMPAIGN_TYPE_LABELS } from '@/constants/billingAndCharges'

export default {
  name: 'ChargesDetails',
  props: {
    modalActive: {
      type: Boolean,
      default: false
    },
    modalData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isActive: this.modalActive
    }
  },
  computed: {
    chargeStatusLabel() {
      const status = this.modalData && this.modalData.charge_status
      return CHARGE_STATUS_LABELS[status] || '-'
    },
    chargeStatusColor() {
      const status = this.modalData && this.modalData.charge_status
      return CHARGE_STATUS_COLORS[status] || 'warning'
    },
    billingStatusLabel() {
      const status = this.modalData && this.modalData.billing_status
      return BILLING_STATUS_LABELS[status] || '-'
    },
    billingStatusColor() {
      const status = this.modalData && this.modalData.billing_status
      return BILLING_STATUS_COLORS[status] || 'warning'
    },
    isCollected() {
      return this.modalData && this.modalData.charge_status === CHARGE_STATUS.COLLECTED
    },
    daysUntilCollection() {
      const days = this.modalData && this.modalData.days_until_collection
      if (days === null || days === undefined) return '-'
      if (days < 0) return `${Math.abs(days)}d (vencido)`
      if (days === 0) return 'Hoy'
      return `${days}d`
    },
    companyName() {
      // Exchange campaigns show the holder (Magnite/PX) to match the table; others keep the billing name
      if (this.modalData && this.modalData.responsible_agency && this.modalData.company) {
        return this.modalData.company.name || '-'
      }
      const bi = this.modalData && this.modalData.billing_information
      return bi ? (bi.fantasy_name || bi.name) : '-'
    },
    chargeTotal() {
      const md = this.modalData
      if (!md) return 0
      if (md.total_to_collect !== null && md.total_to_collect !== undefined) return md.total_to_collect
      if (md.summary && md.summary.billing_amount !== null && md.summary.billing_amount !== undefined) return md.summary.billing_amount
      return md.amount
    },
    companyLegalName() {
      const bi = this.modalData && this.modalData.billing_information
      if (!bi) return null
      return bi.fantasy_name && bi.name !== bi.fantasy_name ? bi.name : null
    },
    billingModeLabel() {
      return BILLING_MODE_LABELS[this.modalData && this.modalData.billing_mode] || 'No informado'
    },
    paymentModeLabel() {
      return PAYMENT_MODE_LABELS[this.modalData && this.modalData.payment_mode] || 'No informado'
    },
    commissionModeLabel() {
      return AGENCY_COMMISSION_MODE_LABELS[this.modalData && this.modalData.agency_commission_mode] || 'No informado'
    },
    campaignTypeLabel() {
      return CAMPAIGN_TYPE_LABELS[this.modalData && this.modalData.campaign_type] || 'No informado'
    }
  },
  watch: {
    modalActive(val) {
      this.isActive = val
    },
    isActive(val) {
      this.$emit('update:modalActive', val)
    }
  },
  methods: {
    formatAmount,
    formatDate,
    formatDateTime,
    formatUserName,
    close() {
      this.isActive = false
    }
  }
}
</script>

<style lang="scss">
@import '@/views/css/billing-charges-details.scss';
</style>

<style scoped>
.billing-status-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #666;
}

.billing-by {
  color: #999;
  font-size: 12px;
}

::v-deep .vs-popup--close {
  display: none !important;
}
</style>
